import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';

const supabaseRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${SUPABASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
};

export class TeamLeadService {
  static async getTeamStats(teamLeadId) {
    try {
      // Try the teamlead_get_team_performance RPC first (this one exists)
      try {
        const data = await supabaseRequest(`/rest/v1/rpc/teamlead_get_team_performance`, {
          method: 'POST',
          body: JSON.stringify({ p_teamlead_id: teamLeadId }),
        });
        const result = (Array.isArray(data) ? data[0] : data) || {};
        // Map the fields to match expected format
        return {
          success: true,
          data: {
            teamSize: result.team_size || 0,
            totalLeads: result.total_leads || 0,
            distributed: result.assigned_leads || 0,
            confirmed: result.converted_leads || 0,
            booked: 0, // Will be calculated separately
            conversionRate: result.conversion_rate || 0,
          },
          error: null,
        };
      } catch (rpcError) {
        // Fallback: Calculate from direct queries
        console.log('RPC failed, calculating stats from direct queries');
        try {
          const [teamMembersResult, bookedOrdersResult, leadsResult] = await Promise.all([
            this.getTeamMembers(teamLeadId).catch(() => ({ success: true, data: [] })),
            this.getBookedOrders(teamLeadId).catch(() => ({ success: true, data: [] })),
            supabaseRequest(`/rest/v1/leads?team_lead_id=eq.${teamLeadId}&select=id,status,assigned_to&limit=10000`).catch(() => []),
          ]);
          
          const teamSize = teamMembersResult?.data?.length || 0;
          const booked = bookedOrdersResult?.data?.length || 0;
          const leads = leadsResult || [];
          
          const totalLeads = leads.length;
          const distributed = leads.filter(l => l.assigned_to).length;
          const confirmed = leads.filter(l => l.status === 'confirmed' || l.status === 'converted').length;
          const conversionRate = totalLeads > 0 ? Math.round((confirmed / totalLeads) * 100) : 0;
          
          return {
            success: true,
            data: {
              teamSize: teamSize,
              totalLeads: totalLeads,
              distributed: distributed,
              confirmed: confirmed,
              booked: booked,
              conversionRate: conversionRate,
            },
            error: null,
          };
        } catch (fallbackError) {
          console.error('Fallback calculation failed:', fallbackError);
          return {
            success: true,
            data: {
              teamSize: 0,
              totalLeads: 0,
              distributed: 0,
              confirmed: 0,
              booked: 0,
              conversionRate: 0,
            },
            error: null,
          };
        }
      }
    } catch (error) {
      console.error('Error fetching team stats:', error);
      return { success: false, data: {}, error: error.message };
    }
  }

  static async getTeamMembers(teamLeadId) {
    try {
      // Method 1: Try the RPC function that the mobile app uses
      try {
        const rpcData = await supabaseRequest(`/rest/v1/rpc/get_telecallers_for_teamlead`, {
          method: 'POST',
          body: JSON.stringify({ p_teamlead_id: teamLeadId }),
        });
        
        if (rpcData && Array.isArray(rpcData) && rpcData.length > 0) {
          return {
            success: true,
            data: rpcData.map(tc => ({
              id: tc.id || tc.telecaller_id,
              name: tc.name || tc.telecaller_name || 'Unknown',
              email: tc.email || 'N/A',
              phone: tc.phone || 'N/A',
              status: tc.status || 'active',
            })),
            error: null,
          };
        }
      } catch (rpcError) {
        console.log('get_telecallers_for_teamlead RPC failed, trying admin_get_telecaller_teamlead_mapping');
      }
      
      // Method 2: Try admin_get_telecaller_teamlead_mapping and filter by team_lead_id
      try {
        const mappingData = await supabaseRequest(`/rest/v1/rpc/admin_get_telecaller_teamlead_mapping`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        
        if (mappingData && Array.isArray(mappingData)) {
          const filtered = mappingData.filter(m => m.teamlead_id === teamLeadId);
          if (filtered.length > 0) {
            return {
              success: true,
              data: filtered.map(tc => ({
                id: tc.telecaller_id,
                name: tc.telecaller_name || 'Unknown',
                email: 'N/A',
                phone: 'N/A',
                status: 'active',
              })),
              error: null,
            };
          }
        }
      } catch (mappingError) {
        console.log('admin_get_telecaller_teamlead_mapping RPC failed, trying user_roles approach');
      }
      
      // Method 3: Get telecallers from user_roles table where assigned_by = teamLeadId
      try {
        const roleData = await supabaseRequest(`/rest/v1/roles?name=eq.telecaller&select=id&limit=1`);
        const telecallerRoleId = roleData?.[0]?.id;
        
        if (telecallerRoleId) {
          const userRolesData = await supabaseRequest(
            `/rest/v1/user_roles?role_id=eq.${telecallerRoleId}&assigned_by=eq.${teamLeadId}&select=user_id&limit=1000`
          );
          
          const telecallerIds = (userRolesData || []).map(ur => ur.user_id).filter(Boolean);
          
          if (telecallerIds.length > 0) {
            // Try to get user details using admin_get_user_by_id RPC one by one
            const userPromises = telecallerIds.slice(0, 20).map(async (id) => {
              try {
                const userData = await supabaseRequest(`/rest/v1/rpc/admin_get_user_by_id`, {
                  method: 'POST',
                  body: JSON.stringify({ p_user_id: id }),
                });
                return Array.isArray(userData) ? userData[0] : userData;
              } catch (err) {
                return null;
              }
            });
            
            const userResults = await Promise.all(userPromises);
            const validUsers = userResults.filter(Boolean);
            
            if (validUsers.length > 0) {
              return {
                success: true,
                data: validUsers.map(u => ({
                  id: u.id,
                  name: u.name || 'Unknown',
                  email: u.email || 'N/A',
                  phone: u.phone || 'N/A',
                  status: u.status || 'active',
                })),
                error: null,
              };
            }
          }
        }
      } catch (userRolesError) {
        console.log('user_roles approach failed:', userRolesError);
      }
      
      // Method 4: Fallback - Get telecaller IDs from leads table
      const leadsData = await supabaseRequest(`/rest/v1/leads?team_lead_id=eq.${teamLeadId}&assigned_to=not.is.null&select=assigned_to&limit=1000`);
      const telecallerIds = [...new Set((leadsData || []).map(l => l.assigned_to).filter(Boolean))];
      
      if (telecallerIds.length === 0) {
        return { success: true, data: [], error: null };
      }
      
      // Final fallback: Return IDs with placeholder names
      return {
        success: true,
        data: telecallerIds.map(id => ({
          id: id,
          name: `Telecaller ${id.slice(0, 8)}`,
          email: 'N/A',
          phone: 'N/A',
          status: 'active',
        })),
        error: null,
      };
    } catch (error) {
      console.error('Error fetching team members:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async distributeLeads(leadIds, telecallerIds) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/teamlead_distribute_leads`, {
        method: 'POST',
        body: JSON.stringify({ p_lead_ids: leadIds, p_telecaller_ids: telecallerIds }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error distributing leads:', error);
      return { success: false, error: error.message };
    }
  }

  static async getBookedOrders(teamLeadId) {
    try {
      // Query master_orders directly for booked orders
      const data = await supabaseRequest(`/rest/v1/master_orders?team_lead_id=eq.${teamLeadId}&booking_status=in.(confirmed,approved,pending)&select=*&order=order_created_at.desc&limit=1000`);
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching booked orders:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getAvailableLeads(teamLeadId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/teamlead_get_available_leads`, {
        method: 'POST',
        body: JSON.stringify({ p_team_lead_id: teamLeadId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching available leads:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getLeadsForConfirmation(teamLeadId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/teamlead_get_leads_for_confirmation`, {
        method: 'POST',
        body: JSON.stringify({ p_team_lead_id: teamLeadId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching leads for confirmation:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async confirmLeads(leadIds, teamLeadId, status, notes) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/teamlead_confirm_leads`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_lead_ids: leadIds, 
          p_team_lead_id: teamLeadId,
          p_status: status,
          p_notes: notes || '',
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error confirming leads:', error);
      return { success: false, error: error.message };
    }
  }

  static async getAvailableTelecallers(teamLeadId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/get_telecallers_for_teamlead`, {
        method: 'POST',
        body: JSON.stringify({ p_teamlead_id: teamLeadId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching available telecallers:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getTelecallerPerformance(teamLeadId, telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/teamlead_get_telecaller_performance`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_team_lead_id: teamLeadId,
          p_telecaller_id: telecallerId 
        }),
      });
      return { success: true, data: (Array.isArray(data) ? data[0] : data) || {}, error: null };
    } catch (error) {
      console.error('Error fetching telecaller performance:', error);
      return { success: false, data: {}, error: error.message };
    }
  }

  static async getApprovalRequests(teamLeadId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/teamlead_get_approval_requests`, {
        method: 'POST',
        body: JSON.stringify({ p_team_lead_id: teamLeadId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching approval requests:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async updateApprovalRequest(requestId, status, notes) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/teamlead_update_approval_request`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_request_id: requestId,
          p_status: status,
          p_notes: notes || '',
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error updating approval request:', error);
      return { success: false, error: error.message };
    }
  }

  static async getLeadDistributionStats(teamLeadId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/teamlead_get_distribution_stats`, {
        method: 'POST',
        body: JSON.stringify({ p_team_lead_id: teamLeadId }),
      });
      return { success: true, data: (Array.isArray(data) ? data[0] : data) || {}, error: null };
    } catch (error) {
      console.error('Error fetching distribution stats:', error);
      return { success: false, data: {}, error: error.message };
    }
  }

  static async assignLeadToTelecaller(teamLeadId, leadId, telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/assign_lead_to_telecaller_no_status`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_teamlead_id: teamLeadId,
          p_lead_id: leadId,
          p_telecaller_id: telecallerId,
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error assigning lead:', error);
      return { success: false, error: error.message };
    }
  }

  static async bulkAssignLeads(teamLeadId, assignments) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/bulk_assign_leads_no_status`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_teamlead_id: teamLeadId,
          p_assignments: assignments,
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error bulk assigning leads:', error);
      return { success: false, error: error.message };
    }
  }
}

